/**
 * Hook for managing the CanvasKit surface lifecycle.
 * Handles init, surface creation/destruction, and container resizing.
 */
import { useEffect, useRef, useState, useCallback, useSyncExternalStore } from 'react';
import type { CanvasKit, Surface } from 'canvaskit-wasm';
import type { FontMgr } from 'canvaskit-wasm/full';
import { getCanvasKit, getFontMgrForUrl, subscribeFonts, getCurrentFontsCss } from '@/lib/canvaskit-init';

export interface SurfaceRefs {
  ck: CanvasKit | null;
  surface: Surface | null;
  fontMgr: FontMgr | null;
  canvas: HTMLCanvasElement | null;
}

export function useCanvasKitSurface(
  containerRef: React.RefObject<HTMLDivElement | null>,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  // Fonts CSS / Google Fonts URL for the document being edited. When provided,
  // the FontMgr is built with these fonts so text renders in the design's actual
  // font (e.g. the imported Figma font) rather than falling back to bundled
  // Roboto. Omit (or undefined) to load Roboto only.
  fontsImportUrl?: string,
) {
  const surfaceRef = useRef<Surface | null>(null);
  const ckRef = useRef<CanvasKit | null>(null);
  const fontMgrRef = useRef<FontMgr | null>(null);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  // Bumped whenever the FontMgr is (re)built so the render loop can redraw with
  // the new fonts (e.g. brand fonts that finish loading after the first paint).
  const [fontEpoch, setFontEpoch] = useState(0);

  // Fonts for this surface: an explicit prop wins, else the app-global fonts CSS
  // (set via setFontFamilies/setFontsImportUrl) read reactively so the FontMgr
  // rebuilds when fonts load after mount.
  const globalFontsCss = useSyncExternalStore(subscribeFonts, getCurrentFontsCss, getCurrentFontsCss);
  const effectiveFontsUrl = fontsImportUrl ?? globalFontsCss;

  // Initialize CanvasKit + FontMgr. Re-runs when the effective fonts change so a
  // late-arriving font set rebuilds the FontMgr (getCanvasKit/getFontMgrForUrl
  // are both cached, so this is cheap on subsequent runs).
  useEffect(() => {
    let disposed = false;
    async function init() {
      try {
        const [ck, fontMgr] = await Promise.all([
          getCanvasKit(),
          getFontMgrForUrl(effectiveFontsUrl),
        ]);
        if (disposed) return;
        ckRef.current = ck;
        fontMgrRef.current = fontMgr;
        setFontEpoch((e) => e + 1);
        setReady(true);
      } catch (err) {
        if (disposed) return;
        console.error('CanvasKit init failed:', err);
        setError('Failed to load CanvasKit WASM');
      }
    }
    init();
    return () => { disposed = true; };
  }, [effectiveFontsUrl]);

  // Container resize observer
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setContainerSize({ w, h });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef]);

  // Create/recreate surface
  useEffect(() => {
    const ck = ckRef.current;
    const canvasEl = canvasRef.current;
    if (!ck || !canvasEl || !ready || containerSize.w <= 0) return;

    if (surfaceRef.current) {
      surfaceRef.current.delete();
      surfaceRef.current = null;
    }

    const dpr = window.devicePixelRatio || 1;
    canvasEl.width = Math.round(containerSize.w * dpr);
    canvasEl.height = Math.round(containerSize.h * dpr);
    canvasEl.style.width = `${containerSize.w}px`;
    canvasEl.style.height = `${containerSize.h}px`;

    let surface = ck.MakeWebGLCanvasSurface(canvasEl);
    if (!surface) {
      surface = ck.MakeSWCanvasSurface(canvasEl);
    }

    if (!surface) {
      setError('Failed to create CanvasKit surface');
      return;
    }

    surfaceRef.current = surface;

    return () => {
      if (surfaceRef.current) {
        surfaceRef.current.delete();
        surfaceRef.current = null;
      }
    };
  }, [ready, containerSize.w, containerSize.h, canvasRef]);

  // surface.delete() releases Skia-side resources but does not free the
  // underlying WebGL context bound to the <canvas>. Browsers cap active
  // contexts (~16 in Chrome) and evict the oldest when full, so leaked
  // contexts eventually corrupt live surfaces with shader compilation
  // errors. Inactive canvas sections render as snapshots (no CanvasKit at
  // all — see lib/canvas-snapshot.ts), so the only context lifecycle that
  // matters now is the active editor's. We defer to the next frame and
  // check isConnected so StrictMode's synchronous mount/cleanup/mount
  // pattern (which keeps the same <canvas> in the DOM) doesn't kill a
  // context that's about to be reused.
  useEffect(() => {
    const canvasEl = canvasRef.current;
    return () => {
      if (!canvasEl) return;
      requestAnimationFrame(() => {
        if (canvasEl.isConnected) return;
        const gl =
          (canvasEl.getContext('webgl2') as WebGL2RenderingContext | null) ??
          (canvasEl.getContext('webgl') as WebGLRenderingContext | null);
        const lose = gl?.getExtension('WEBGL_lose_context');
        lose?.loseContext();
      });
    };
  }, [canvasRef]);

  const getRefs = useCallback((): SurfaceRefs => ({
    ck: ckRef.current,
    surface: surfaceRef.current,
    fontMgr: fontMgrRef.current,
    canvas: canvasRef.current,
  }), [canvasRef]);

  return { ready, error, containerSize, getRefs, surfaceRef, fontEpoch };
}
